import { useEffect, useState } from "react";
import type { Api } from "../api.ts";
import type { Glyph } from "../items.ts";
import type { ElementReference } from "../types.ts";
import { useAssetUrl } from "../useAsset.ts";
import { useApp } from "./app.ts";
import { Icon } from "./icons.tsx";

const refCache = new Map<string, Promise<ElementReference[]>>();

/** References of one element version, cached per session (invalidate after an upload). */
export function elementRefs(api: Api, ws: string, id: string, v: number): Promise<ElementReference[]> {
  const k = `${ws}:${id}:${v}`;
  let p = refCache.get(k);
  if (!p) {
    p = api.listReferences(ws, id, v);
    refCache.set(k, p);
    p.catch(() => refCache.delete(k));
  }
  return p;
}
export const forgetElementRefs = (ws: string, id: string, v: number) => refCache.delete(`${ws}:${id}:${v}`);

/** The picture a card shows for an element: a main (canonical) image first, the newest one first. */
export function coverReference(refs: readonly ElementReference[]): ElementReference | undefined {
  return [...refs]
    .filter((r) => r.role !== "voice" && r.role !== "lora")
    .sort((a, b) => Number(b.canonical) - Number(a.canonical) || b.created_at.localeCompare(a.created_at))[0];
}

/** `element:<id>:<version>` → the asset version of its thumbnail reference; anything else passes through. */
function useResolved(thumb: string | null): { id: string | null; pending: boolean } {
  const { api, ws } = useApp();
  const isEl = !!thumb?.startsWith("element:");
  const [state, setState] = useState<{ key: string | null; id: string | null }>({ key: null, id: null });
  useEffect(() => {
    if (!thumb || !isEl) return;
    const [, id, v] = thumb.split(":");
    let live = true;
    elementRefs(api, ws, id!, Number(v) || 1).then(
      (refs) => live && setState({ key: thumb, id: coverReference(refs)?.asset_version_id ?? null }),
      () => live && setState({ key: thumb, id: null }),
    );
    return () => {
      live = false;
    };
  }, [api, ws, thumb, isEl]);
  if (!isEl) return { id: thumb, pending: false };
  return state.key === thumb ? { id: state.id, pending: false } : { id: null, pending: true };
}

/**
 * One still: the asset's thumbnail (or full image), a shimmer while loading, and a quiet glyph when the
 * item has no picture.
 */
export function Picture({
  thumb,
  alt,
  glyph,
  className = "",
  full = false,
  decorative = false,
}: {
  thumb: string | null;
  alt: string;
  glyph: Glyph;
  className?: string;
  /** use the full image instead of the thumbnail rendition */
  full?: boolean;
  /** the picture says nothing the text beside it does not: hidden from assistive technology */
  decorative?: boolean;
}) {
  const { api, ws } = useApp();
  const r = useResolved(thumb);
  const { url, error } = useAssetUrl(api, ws, r.id);
  const [broken, setBroken] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<string | null>(null);
  const cls = `pic ${className}`;
  if (r.pending || (r.id && !url && !error))
    return decorative ? (
      <div className={`${cls} pic-loading`} aria-hidden="true" />
    ) : (
      <div className={`${cls} pic-loading`} role="img" aria-label={`${alt}, loading`} />
    );
  const src = url ? (full ? url.url : (url.thumbnail_url ?? url.url)) : null;
  const srcSet =
    !full && url?.thumbnails?.length
      ? url.thumbnails.map((t) => `${t.url} ${t.width}w`).join(", ")
      : undefined;
  if (!src || broken === src)
    return decorative ? (
      <div className={`${cls} pic-empty`} aria-hidden="true">
        <EmptyFace glyph={glyph} />
      </div>
    ) : (
      <div className={`${cls} pic-empty`} role="img" aria-label={`${alt}, no picture yet`}>
        <EmptyFace glyph={glyph} />
      </div>
    );
  return (
    <div className={cls} aria-hidden={decorative || undefined}>
      <img
        ref={(el) => {
          // from the cache the picture can be complete before React listens for its load
          if (el?.complete && el.naturalWidth > 0 && loaded !== src) setLoaded(src);
        }}
        className={loaded === src ? undefined : "pic-wait"}
        src={src}
        srcSet={srcSet}
        sizes={srcSet ? "auto, 400px" : undefined}
        alt={alt}
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={() => setLoaded(src)}
        onError={() => setBroken(src)}
      />
    </div>
  );
}

const NINE = ["a", "b", "c", "d", "e", "f", "g", "h", "i"] as const;

/**
 * What a frame shows before there is a picture: on a still of some size, the nine squares of a production as
 * open outlines (the square fills once the work is signed off, BRAND.md); on a small one, the item's glyph.
 * Which one shows is a container query on the frame (styles.css, `.pic-empty`).
 */
function EmptyFace({ glyph }: { glyph: Glyph }) {
  return (
    <>
      <span className="pic-empty-mark">
        {NINE.map((k) => (
          <span key={k} />
        ))}
      </span>
      <Icon name={glyph} size={22} />
    </>
  );
}
